import React, { useEffect, useRef } from 'react';

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

const O  = '#FF7A1A'
const O2 = '#FF9340'
const O3 = 'rgba(255,122,26,'

export default function NetworkCanvas() {
  const ref = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1
    let animId = 0, t = 0

    const resize = () => {
      canvas.width  = canvas.offsetWidth  * dpr
      canvas.height = canvas.offsetHeight * dpr
      ctx.scale(dpr, dpr)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    const W = () => canvas.offsetWidth
    const H = () => canvas.offsetHeight

    const mkNodes = () => {
      const w = W(), h = H()
      return [
        { x: w*.50, y: h*.44, type:'server', pulse:0, pulseSpd:.009, active:true, timer:0 },
        { x: w*.14, y: h*.18, type:'pc',     pulse:Math.random(), pulseSpd:.007, active:true,  timer:0 },
        { x: w*.33, y: h*.13, type:'pc',     pulse:Math.random(), pulseSpd:.008, active:true,  timer:0 },
        { x: w*.67, y: h*.13, type:'pc',     pulse:Math.random(), pulseSpd:.007, active:true,  timer:0 },
        { x: w*.86, y: h*.18, type:'pc',     pulse:Math.random(), pulseSpd:.008, active:false, timer:0 },
        { x: w*.08, y: h*.56, type:'laptop', pulse:Math.random(), pulseSpd:.009, active:true,  timer:0 },
        { x: w*.23, y: h*.73, type:'laptop', pulse:Math.random(), pulseSpd:.007, active:true,  timer:0 },
        { x: w*.77, y: h*.73, type:'laptop', pulse:Math.random(), pulseSpd:.008, active:false, timer:0 },
        { x: w*.92, y: h*.56, type:'laptop', pulse:Math.random(), pulseSpd:.009, active:true,  timer:0 },
      ]
    }
    let nodes = mkNodes()

    const mkPkts = () => {
      const p = []
      for (let i = 1; i < 9; i++) {
        p.push({ from: i, to: 0, t: Math.random(), spd: .002 + Math.random()*.003 })
        if (Math.random() > .5) p.push({ from: 0, to: i, t: Math.random(), spd: .0018 + Math.random()*.003 })
      }
      return p
    }
    let pkts = mkPkts()

    const particles = Array.from({ length: 65 }, () => ({
      x: Math.random() * 1200, y: Math.random() * 700,
      vx: (Math.random()-.5)*.3, vy: (Math.random()-.5)*.3,
      r: .5 + Math.random()*1.8, o: Math.random()*.5+.1, od: Math.random()>.5?1:-1,
    }))

    const mkGraphs = () => {
      const w = W(), h = H()
      return [
        { x: w*.62, y: h*.24, fy: 0, fspd:.015, bars: Array.from({length:9}, ()=>Math.random()) },
        { x: w*.09, y: h*.35, fy: 0, fspd:.013, bars: Array.from({length:8}, ()=>Math.random()) },
        { x: w*.43, y: h*.72, fy: 0, fspd:.016, bars: Array.from({length:9}, ()=>Math.random()) },
      ]
    }
    let graphs = mkGraphs()

    // ── Draw grid ──
    const drawGrid = (w, h) => {
      ctx.strokeStyle = 'rgba(255,122,26,0.06)'
      ctx.lineWidth = .5
      const sz = 40
      for (let x = 0; x < w; x += sz) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,h); ctx.stroke() }
      for (let y = 0; y < h; y += sz) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(w,y); ctx.stroke() }
    }

    // ── Draw connection line ──
    const drawLine = (a, b, pulse) => {
      const alpha = .18 + pulse * .28
      const g = ctx.createLinearGradient(a.x, a.y, b.x, b.y)
      g.addColorStop(0, O3 + (alpha*.5) + ')')
      g.addColorStop(.5, O3 + alpha + ')')
      g.addColorStop(1, O3 + (alpha*.5) + ')')
      ctx.beginPath(); ctx.strokeStyle = g; ctx.lineWidth = 1
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
      ctx.beginPath(); ctx.strokeStyle = O3 + (alpha*.1) + ')'; ctx.lineWidth = 5
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
    }

    // ── Draw server ──
    const drawServer = (n) => {
      const { x, y } = n
      const sw = 32, sh = 22

      // Pulse rings
      for (let i = 0; i < 3; i++) {
        const p = (n.pulse + i/3) % 1
        ctx.beginPath()
        ctx.arc(x, y, 30 + p*55, 0, Math.PI*2)
        ctx.strokeStyle = O3 + ((1-p)*.2) + ')'
        ctx.lineWidth = 1; ctx.stroke()
      }

      // Body
      ctx.shadowColor = O; ctx.shadowBlur = 22
      rr(ctx, x-sw, y-sh, sw*2, sh*2, 7)
      const bg = ctx.createLinearGradient(x-sw, y-sh, x+sw, y+sh)
      bg.addColorStop(0, 'rgba(45,22,0,0.8)')
      bg.addColorStop(1, 'rgba(20,10,0,0.8)')
      ctx.fillStyle = bg; ctx.fill()
      ctx.strokeStyle = O3+'.7)'; ctx.lineWidth = 1.5; ctx.stroke()

      // Rack slots
      for (let i = 0; i < 4; i++) {
        rr(ctx, x-sw+5, y-sh+5+i*9, sw*2-10, 6, 2)
        ctx.fillStyle = 'rgba(255,122,26,0.12)'; ctx.fill()
        ctx.strokeStyle = O3+'.3)'; ctx.lineWidth = .5; ctx.stroke()
        // LED
        ctx.beginPath(); ctx.arc(x+sw-10, y-sh+8+i*9, 2, 0, Math.PI*2)
        ctx.fillStyle = i%2===0 ? O : O2
        ctx.shadowColor = O; ctx.shadowBlur = 8; ctx.fill()
      }

      ctx.shadowBlur = 0
      ctx.fillStyle = O3+'.7)'; ctx.font = '600 8px Inter,sans-serif'
      ctx.textAlign = 'center'; ctx.fillText('MONITOR SERVER', x, y+sh+12); ctx.textAlign = 'left'
    }

    // ── Draw PC/laptop ──
    const drawNode = (n) => {
      const { x, y, active } = n
      const mw = 18, mh = 13
      const color = active ? O : 'rgba(255,122,26,0.4)'

      ctx.shadowColor = O; ctx.shadowBlur = active ? 14 : 6
      rr(ctx, x-mw, y-mh, mw*2, mh*1.7, 3)
      ctx.fillStyle = active ? 'rgba(35,16,0,0.7)' : 'rgba(18,8,0,0.5)'; ctx.fill()
      ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.stroke()

      // Screen
      ctx.fillStyle = active ? `rgba(255,122,26,${.1+n.pulse*.12})` : 'rgba(255,122,26,0.05)'
      ctx.fillRect(x-mw+3, y-mh+3, mw*2-6, mh-2)

      // Screen scanlines
      if (active) {
        ctx.strokeStyle = O3+'.18)'; ctx.lineWidth = .5
        for (let sy = y-mh+5; sy < y+2; sy += 3) {
          ctx.beginPath(); ctx.moveTo(x-mw+3, sy); ctx.lineTo(x+mw-3, sy); ctx.stroke()
        }
      }

      // Stand
      ctx.strokeStyle = O3+(active?.45:.2)+')'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x, y+mh*.7); ctx.lineTo(x, y+mh*.7+6); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x-6, y+mh*.7+6); ctx.lineTo(x+6, y+mh*.7+6); ctx.stroke()

      // Activity dot
      if (active) {
        const dp = (Math.sin(t*2.5+n.pulse*8)+1)/2
        ctx.beginPath(); ctx.arc(x+mw-5, y-mh+5, 2.5, 0, Math.PI*2)
        ctx.fillStyle = O3+(0.5+dp*0.5)+')'; ctx.shadowBlur = 6+dp*8; ctx.fill()
      }

      ctx.shadowBlur = 0
    }

    // ── Draw packet ──
    const drawPkt = (p) => {
      const a = nodes[p.from], b = nodes[p.to]
      const px = a.x + (b.x-a.x)*p.t
      const py = a.y + (b.y-a.y)*p.t
      for (let i = 1; i <= 6; i++) {
        const pt = p.t - i*.018
        if (pt<0||pt>1) continue
        const tx = a.x+(b.x-a.x)*pt, ty = a.y+(b.y-a.y)*pt
        ctx.beginPath(); ctx.arc(tx, ty, 2.5*(1-i/7), 0, Math.PI*2)
        ctx.fillStyle = O; ctx.globalAlpha = (1-i/7)*.4; ctx.fill()
        ctx.globalAlpha = 1
      }
      ctx.shadowColor = O; ctx.shadowBlur = 14
      ctx.beginPath(); ctx.arc(px, py, 3, 0, Math.PI*2); ctx.fillStyle = O; ctx.fill()
      ctx.shadowBlur = 0
    }

    // ── Draw mini graph ──
    const drawGraph = (g) => {
      const w2 = 54, h2 = 36
      const fy = g.y + Math.sin(g.fy)*5
      ctx.shadowColor = O; ctx.shadowBlur = 18
      rr(ctx, g.x, fy, w2, h2, 5)
      const gb = ctx.createLinearGradient(g.x, fy, g.x+w2, fy+h2)
      gb.addColorStop(0, 'rgba(22,12,0,0.92)')
      gb.addColorStop(1, 'rgba(11,6,0,0.92)')
      ctx.fillStyle = gb; ctx.fill()
      ctx.strokeStyle = O3+'.4)'; ctx.lineWidth = 1; ctx.stroke()
      ctx.shadowBlur = 0

      ctx.fillStyle = O3+'.65)'; ctx.font = '500 7px Inter,sans-serif'; ctx.textAlign = 'left'
      ctx.fillText('NET TRAFFIC', g.x+5, fy+9)
      ctx.fillStyle = 'rgba(247,247,247,.55)'; ctx.font = '600 8px Inter,sans-serif'; ctx.textAlign = 'right'
      ctx.fillText(`${Math.round(30+Math.sin(t+g.fy)*35)}%`, g.x+w2-5, fy+9)

      const bcount = g.bars.length
      const bw = (w2-12)/bcount-1.5
      for (let i = 0; i < bcount; i++) {
        const bh = (Math.sin(t*1.4+i*.8+g.fy)*.5+.5)*(h2-18)
        const bx = g.x+6+i*(bw+1.5), by = fy+h2-5-bh
        const bg = ctx.createLinearGradient(bx, by, bx, by+bh)
        bg.addColorStop(0, O3+'.9)'); bg.addColorStop(1, O3+'.4)')
        ctx.fillStyle = bg; ctx.fillRect(bx, by, bw, bh)
      }
      ctx.textAlign = 'left'
    }

    // ── Scan line ──
    const drawScan = (w, h) => {
      const sy = ((t*40)%(h+80))-40
      const sg = ctx.createLinearGradient(0, sy-3, 0, sy+3)
      sg.addColorStop(0, 'transparent')
      sg.addColorStop(.5, O3+'.04)')
      sg.addColorStop(1, 'transparent')
      ctx.fillStyle = sg; ctx.fillRect(0, sy-3, w, 6)
    }

    const draw = () => {
      t += .011
      const w = W(), h = H()
      ctx.clearRect(0, 0, w, h)

      // Ambient
      const ag = ctx.createRadialGradient(w*.5, h*.44, 0, w*.5, h*.44, w*.5)
      ag.addColorStop(0, 'rgba(80,35,0,0.1)'); ag.addColorStop(1, 'rgba(11,11,12,0)')
      ctx.fillStyle = ag; ctx.fillRect(0, 0, w, h)

      drawGrid(w, h)

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy
        p.o += p.od * .005
        if (p.o>=.65||p.o<=.05) p.od*=-1
        if (p.x<0) p.x=w; if (p.x>w) p.x=0
        if (p.y<0) p.y=h; if (p.y>h) p.y=0
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2)
        ctx.fillStyle = O3+(p.o*.5)+')'; ctx.fill()
      })

      for (let i = 1; i < nodes.length; i++) {
        const pulse = (Math.sin(t*1.4+i*.8)+1)/2
        drawLine(nodes[0], nodes[i], pulse)
      }

      graphs.forEach(g => { g.fy += g.fspd; drawGraph(g) })

      nodes[0].pulse = (nodes[0].pulse+nodes[0].pulseSpd)%1
      drawServer(nodes[0])

      for (let i = 1; i < nodes.length; i++) {
        nodes[i].pulse = (nodes[i].pulse+nodes[i].pulseSpd)%1
        nodes[i].timer += .008
        if (nodes[i].timer > 3+Math.random()*5) { nodes[i].active = !nodes[i].active; nodes[i].timer=0 }
        drawNode(nodes[i])
      }

      pkts.forEach(p => {
        p.t += p.spd; if (p.t>=1) { p.t=0; p.spd=.002+Math.random()*.003 }
        drawPkt(p)
      })

      drawScan(w, h)
      animId = requestAnimationFrame(draw)
    }

    draw()
    return () => { cancelAnimationFrame(animId); ro.disconnect() }
  }, [])

  return <canvas ref={ref} style={{ width:'100%', height:'100%', display:'block' }} />
}
